import { Page, Text, View, Document, StyleSheet, PDFDownloadLink, } from '@react-pdf/renderer';
import React, { useEffect, useState } from 'react';
import { getAll } from '../../services/Users/Users';
import { displayDate, displayOutDate } from '../../utils/date';

const styles = StyleSheet.create({
    page: {
        flexDirection: 'column',
        backgroundColor: '#FFFFFF',
        padding: 24,
    },
    title: {
        fontSize: 20,
        marginBottom: 16,
        textAlign: 'center',
        color: '#222',
    },
    sectionHeader: {
        flexDirection: 'row',
        alignItems: 'baseline',
        gap: 12,
        marginBottom: 8,
    },
    sectionTitle: { fontSize: 14, fontWeight: 'bold' },
    date: { fontSize: 10, color: '#444' },

    // Tabla
    table: {
        borderWidth: 1,
        borderColor: '#cfcfcf',
        borderStyle: 'solid',
        borderRadius: 6,
    },
    headerRow: {
        flexDirection: 'row',
        backgroundColor: '#d6d6d6',
        borderBottomWidth: 1,
        borderBottomColor: '#cfcfcf',
    },
    bodyRow: {
        flexDirection: 'row',
    },
    rowAlt: { backgroundColor: '#f6f6f6' },
    // Columna contenedor (sin bordes generales para evitar “doble línea”)
    col: {
        paddingVertical: 6,
        paddingHorizontal: 6,
        justifyContent: 'center',
    },
    // Divisor vertical (usamos borde derecho únicamente)
    colDivider: {
        borderRightWidth: 1,
        borderRightColor: '#e1e1e1',
        borderRightStyle: 'solid',
    },
    // Separador horizontal entre filas
    rowDivider: {
        borderBottomWidth: 1,
        borderBottomColor: '#eeeeee',
        borderBottomStyle: 'solid',
    },

    th: { fontSize: 9, fontWeight: 'bold', textAlign: 'center' },
    td: { fontSize: 8, textAlign: 'center', lineHeight: 1.2 },
    tdLeft: { textAlign: 'left' },
});

/* ============== FECHA / FILE NAME ============== */
const now = new Date();
const headerDate = now.toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' });

/* ============== UTILES ============== */
const S = (...s) => s.filter(Boolean); // fusiona estilos evitando undefined
const safe = (x) => String(x ?? '');

/* ============== DOCUMENTO ============== */
const MyReport = ({ data = [], folioMap = {} }) => {
    const [users, setUsers] = useState([]);

    useEffect(() => {
        getAll().then((list) => setUsers(list || []));
    }, []);

    const rows = Array.isArray(data) ? data : [];

    const COLS = {
        nombre: 3,
        clave: 2,
        descripcion: 3.5,
        fechaEntrada: 2,
        fechaSalida: 2,
        categoria: 2,
        cantidad: 1.5,
        folios: 3,
        ubicacion: 2,
        estado: 2,
        concepto: 2,
        costo: 2,
        usuario: 1.5,
    };

    const getUserName = (id) => users.find((u) => u.id === id)?.userName ?? '—';

    const fmtEntrada = (it) => it.acquisitionDateHumanFormatted || displayDate(it.acquisitionDate, '');
    const fmtSalida = (it) => displayOutDate(it.outDate);

    const conceptLabel = (it) => {
        const t = String(it?.movementType || '').trim().toUpperCase();
        const map = {
            SALE: 'Venta',
            COURTESY: 'Cortesía',
            WASTE: 'Merma',
            PROMOTION: 'Promoción',
            TASTING: 'Degustación',
        };
        return map[t] || '—';
    };

    const formatFolio = (r) => {
        const count = Number(r.count ?? 0);
        const isSingle = count <= 1 || r.startFolioName === r.endFolioName;
        return isSingle
            ? r.startFolioName
            : `${r.startFolioName} – ${r.endFolioName} (${count})`;
    };

    const getReportFolios = (it) => {
        const ranges = folioMap?.[it.id] || [];
        return ranges.map(formatFolio);
    };

    const getReportFoliosText = (it) => {
        const ranges = folioMap?.[it.id] || [];
        const labels = ranges.map(formatFolio);
        return labels.length ? labels.join(' | ') : '—';
    };

    return (
        <Document>
            <Page size="A4" orientation="landscape" style={styles.page}>
                <Text style={styles.title}>Reporte de Inventario</Text>

                <View style={styles.sectionHeader}>
                    <Text style={styles.sectionTitle}>Resumen</Text>
                    <Text style={styles.date}>{headerDate}</Text>
                </View>

                {/* TABLA */}
                <View style={styles.table}>
                    {/* Encabezado */}
                    <View style={styles.headerRow}>
                        <View style={[styles.col, styles.colDivider, { flex: COLS.nombre }]}><Text style={styles.th}>Nombre</Text></View>
                        <View style={[styles.col, styles.colDivider, { flex: COLS.clave }]}><Text style={styles.th}>Clave</Text></View>
                        <View style={[styles.col, styles.colDivider, { flex: COLS.descripcion }]}><Text style={styles.th}>Descripción</Text></View>
                        <View style={[styles.col, styles.colDivider, { flex: COLS.fechaEntrada }]}><Text style={styles.th}>Fecha entrada</Text></View>
                        <View style={[styles.col, styles.colDivider, { flex: COLS.fechaSalida }]}><Text style={styles.th}>Fecha salida</Text></View>
                        <View style={[styles.col, styles.colDivider, { flex: COLS.categoria }]}><Text style={styles.th}>Categoría</Text></View>
                        <View style={[styles.col, styles.colDivider, { flex: COLS.cantidad }]}><Text style={styles.th}>Cantidad</Text></View>
                        <View style={[styles.col, styles.colDivider, { flex: COLS.folios }]}><Text style={styles.th}>Folios</Text></View>
                        <View style={[styles.col, styles.colDivider, { flex: COLS.ubicacion }]}><Text style={styles.th}>Ubicación</Text></View>
                        <View style={[styles.col, styles.colDivider, { flex: COLS.estado }]}><Text style={styles.th}>Estado</Text></View>
                        <View style={[styles.col, styles.colDivider, { flex: COLS.concepto }]}><Text style={styles.th}>Concepto</Text></View>
                        <View style={[styles.col, styles.colDivider, { flex: COLS.costo }]}><Text style={styles.th}>Costo unitario</Text></View>
                        <View style={[styles.col, { flex: COLS.usuario }]}><Text style={styles.th}>Asignado a</Text></View>
                    </View>

                    {/* Filas */}
                    {rows.map((it, i) => {
                        return (
                            <View
                                key={it.id ?? i}
                                style={S(
                                    styles.bodyRow,
                                    i < rows.length - 1 && styles.rowDivider,
                                    i % 2 === 1 && styles.rowAlt
                                )}
                            >
                                <View style={[styles.col, styles.colDivider, { flex: COLS.nombre }]}>
                                    <Text style={S(styles.td, styles.tdLeft)}>{safe(it.name)}</Text>
                                </View>

                                <View style={[styles.col, styles.colDivider, { flex: COLS.clave }]}>
                                    <Text style={styles.td}>{safe(it.supplyCodes)}</Text>
                                </View>

                                <View style={[styles.col, styles.colDivider, { flex: COLS.descripcion }]}>
                                    <Text style={S(styles.td, styles.tdLeft)}>{safe(it.description)}</Text>
                                </View>

                                <View style={[styles.col, styles.colDivider, { flex: COLS.fechaEntrada }]}>
                                    <Text style={styles.td}>{safe(fmtEntrada(it))}</Text>
                                </View>

                                <View style={[styles.col, styles.colDivider, { flex: COLS.fechaSalida }]}>
                                    <Text style={styles.td}>{safe(fmtSalida(it))}</Text>
                                </View>

                                <View style={[styles.col, styles.colDivider, { flex: COLS.categoria }]}>
                                    <Text style={styles.td}>{safe(it.categoryName)}</Text>
                                </View>

                                <View style={[styles.col, styles.colDivider, { flex: COLS.cantidad }]}>
                                    <Text style={styles.td}>
                                        {`${Number(it.quantity).toLocaleString('es-MX')} ${safe(it.unitOfWeightMeasurement || '')}`}
                                    </Text>
                                </View>

                                <View style={[styles.col, styles.colDivider, { flex: COLS.folios }]}>
                                    <Text style={S(styles.td, styles.tdLeft)}>
                                        {getReportFoliosText(it)}
                                    </Text>
                                </View>

                                <View style={[styles.col, styles.colDivider, { flex: COLS.ubicacion }]}>
                                    <Text style={styles.td}>{safe(it.locationName)}</Text>
                                </View>

                                <View style={[styles.col, styles.colDivider, { flex: COLS.estado }]}>
                                    <Text style={styles.td}>{safe(it.statusName)}</Text>
                                </View>

                                <View style={[styles.col, styles.colDivider, { flex: COLS.concepto }]}>
                                    <Text style={styles.td}>{conceptLabel(it)}</Text>
                                </View>

                                <View style={[styles.col, styles.colDivider, { flex: COLS.costo }]}>
                                    <Text style={styles.td}>{safe(it.unitPrice)}</Text>
                                </View>

                                <View style={[styles.col, { flex: COLS.usuario }]}>
                                    <Text style={styles.td}>{safe(getUserName(it.assignedToUserId))}</Text>
                                </View>
                            </View>
                        );
                    })}
                </View>
            </Page>
        </Document>
    );
};
/* --------------------------------------------------- */

export { MyReport };